import { backendClient } from '../lib/api/backendClient';
import { assertSpotifyUserId } from '../lib/auth/spotifyUserId';

export interface FavoriteWork {
  id: string;
  workId: string;
  savedAt: string;
  notes?: string;
}

const toIsoString = (value?: string | Date): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value.toISOString();
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? undefined : parsed.toISOString();
};

const deserializeFavoriteWork = (payload: any): FavoriteWork => {
  const stringOrUndefined = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length ? value : undefined;

  const savedAt = typeof payload?.savedAt === 'string' ? payload.savedAt : undefined;
  const fallback =
    (typeof payload?.updated_at === 'string' && payload.updated_at) ||
    (typeof payload?.created_at === 'string' && payload.created_at) ||
    new Date().toISOString();

  const workId =
    stringOrUndefined(payload?.workId) ??
    stringOrUndefined(payload?.work_id) ??
    stringOrUndefined(payload?.id) ??
    '';

  return {
    id: stringOrUndefined(payload?.id) ?? workId,
    workId,
    notes: typeof payload?.notes === 'string' ? payload.notes : undefined,
    savedAt: toIsoString(savedAt) ?? fallback,
  };
};

export const favoriteWorksService = {
  async getFavoriteWorks(): Promise<FavoriteWork[]> {
    const userId = assertSpotifyUserId();
    const { data } = await backendClient.get<{ works?: any[] }>('/favorites/works', {
      params: { userId }
    });
    const works = Array.isArray(data?.works) ? data.works : [];
    return works.map(deserializeFavoriteWork);
  },

  async getFavoriteWork(workId: string): Promise<FavoriteWork | null> {
    if (!workId) return null;
    try {
      const { data } = await backendClient.get<{ work?: any }>(
        `/favorites/works/${encodeURIComponent(workId)}`
      );
      if (!data?.work) return null;
      return deserializeFavoriteWork(data.work);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        const favorites = await favoriteWorksService.getFavoriteWorks();
        return favorites.find((favorite) => favorite.workId === workId) ?? null;
      }
      throw error;
    }
  },

  async saveFavoriteWork(
    workId: string,
    options?: { notes?: string; savedAt?: string | Date; id?: string }
  ) {
    const userId = assertSpotifyUserId();
    const payload: Record<string, unknown> = { workId };
    if (options?.notes) payload.notes = options.notes;
    if (options?.savedAt) payload.savedAt = toIsoString(options.savedAt);
    if (options?.id) payload.id = options.id;
    payload.userId = userId;

    const { data } = await backendClient.post<{ work: any }>('/favorites/works', payload);
    return deserializeFavoriteWork(data.work);
  },

  async updateFavoriteWork(
    workId: string,
    options?: { notes?: string; savedAt?: string | Date; id?: string }
  ) {
    const userId = assertSpotifyUserId();
    const payload: Record<string, unknown> = {};
    if (options?.notes) payload.notes = options.notes;
    if (options?.savedAt) payload.savedAt = toIsoString(options.savedAt);
    if (options?.id) payload.id = options.id;
    payload.userId = userId;

    try {
      const { data } = await backendClient.put<{ work: any }>(
        `/favorites/works/${encodeURIComponent(workId)}`,
        payload
      );
      return deserializeFavoriteWork(data.work);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        const { data } = await backendClient.post<{ work: any }>('/favorites/works', {
          ...payload,
          workId,
        });
        return deserializeFavoriteWork(data.work);
      }
      throw error;
    }
  },

  async removeFavoriteWork(workId: string): Promise<void> {
    if (!workId) return;
    const userId = assertSpotifyUserId();
    await backendClient.delete(`/favorites/works/${encodeURIComponent(workId)}`, {
      params: { userId }
    });
  },
};
